import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Share,
  Plus,
  BellRing,
  Smartphone,
  CheckCircle2,
  Zap,
  X,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { BrandLogoImg } from "@/components/BrandLogoImg";
import {
  enablePushForSchool,
  enablePushForAdmin,
  pushSupported,
  getPushPermission,
} from "@/lib/push";

/** Detects iOS devices (iPhone / iPad / iPod). */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/** True when the app is launched from Home Screen (PWA installed). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

/** True if browser supports the beforeinstallprompt event (Chrome / Edge / Android). */
export function supportsNativeInstall() {
  return typeof window !== "undefined" && !!window.__amcDeferredInstallPrompt;
}

export default function InstallAppDialog({ open, onOpenChange, role = "school" }) {
  const ios = isIOS();
  const [standalone, setStandalone] = useState(isStandalone());
  const [perm, setPerm] = useState("default");
  const [busy, setBusy] = useState(false);
  const [, forceRender] = useState(0);

  // Keep "canInstall" fresh if the beforeinstallprompt fires after the dialog opens
  useEffect(() => {
    const bump = () => forceRender((x) => x + 1);
    window.addEventListener("amc:install-available", bump);
    return () => window.removeEventListener("amc:install-available", bump);
  }, []);

  // Auto-close dialog as soon as the OS confirms installation
  useEffect(() => {
    const onInstalled = () => {
      setStandalone(true);
      try {
        localStorage.setItem("amc-pwa-installed", "1");
      } catch {}
      onOpenChange?.(false);
      toast.success("App instalado com sucesso!");
    };
    window.addEventListener("amc:app-installed", onInstalled);
    return () => window.removeEventListener("amc:app-installed", onInstalled);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      if (!pushSupported()) return setPerm("unsupported");
      setPerm(await getPushPermission());
    })();
  }, [open]);

  const enablePush = async () => {
    setBusy(true);
    const fn = role === "admin" ? enablePushForAdmin : enablePushForSchool;
    const r = await fn();
    setBusy(false);
    if (r.ok) {
      setPerm("granted");
      toast.success("Notificações ativadas!");
    } else if (r.reason === "denied") {
      setPerm("denied");
      toast.error("Permissão negada no navegador");
    } else {
      toast.error("Não foi possível ativar notificações");
    }
  };

  const triggerNativeInstall = async () => {
    const dp = window.__amcDeferredInstallPrompt;
    if (!dp) return;
    // Close the dialog right away — the native OS prompt takes over the screen
    onOpenChange?.(false);
    try {
      dp.prompt();
      const choice = await dp.userChoice;
      if (choice?.outcome === "dismissed") {
        toast.message("Instalação cancelada");
      }
    } catch {}
    window.__amcDeferredInstallPrompt = null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[420px] p-0 overflow-hidden rounded-3xl border-0 shadow-[0_30px_80px_-20px_rgba(30,58,138,0.55)] [&>button:last-child]:hidden"
        data-testid="install-app-dialog"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-[#1e3a8a] via-[#4338ca] to-[#7c3aed] text-white px-6 pt-7 pb-10 overflow-hidden">
          {/* Decorative glows */}
          <div className="pointer-events-none absolute -top-20 -right-16 w-56 h-56 rounded-full bg-fuchsia-400/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-sky-400/25 blur-3xl" />
          {/* Dot pattern */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "14px 14px",
            }}
          />

          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            aria-label="Fechar"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center backdrop-blur-sm transition"
            data-testid="install-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-xl ring-4 ring-white/20 flex items-center justify-center overflow-hidden">
              <BrandLogoImg
                alt="AMC Escolas"
                className="w-11 h-11 object-contain"
              />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                App Oficial
              </div>
              <DialogTitle className="text-white font-heading text-[22px] font-black tracking-tight leading-tight mt-1">
                Instalar AMC Escolas
              </DialogTitle>
            </div>
          </div>

          <p className="relative text-[13px] text-white/90 mt-3 leading-snug">
            Tenha o portal da escola na palma da mão, com acesso rápido e
            notificações em tempo real.
          </p>
        </div>

        {/* Body — overlapping card */}
        <div className="relative -mt-5 bg-white rounded-t-3xl px-5 pt-5 pb-5 max-h-[65vh] overflow-y-auto">
          {standalone ? (
            <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/30">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-sm text-emerald-800">
                  App já instalado!
                </p>
                <p className="text-[11px] text-emerald-700">
                  Agora ative as notificações abaixo.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <BenefitCard
                  icon={Zap}
                  label="Acesso rápido"
                  tint="from-amber-400 to-orange-500"
                />
                <BenefitCard
                  icon={Smartphone}
                  label="Tela cheia, como app"
                  tint="from-sky-400 to-blue-600"
                />
                <BenefitCard
                  icon={BellRing}
                  label="Notificações"
                  tint="from-fuchsia-500 to-purple-600"
                />
              </div>

              {ios ? (
                <IOSSteps />
              ) : (
                <AndroidDesktopSteps onInstall={triggerNativeInstall} />
              )}
            </>
          )}

          {/* Push section */}
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white flex items-center justify-center">
                <BellRing className="w-3.5 h-3.5" />
              </div>
              <p className="font-heading font-black text-sm text-[#1e3a8a]">
                Ativar notificações
              </p>
            </div>
            {perm === "granted" ? (
              <p
                className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2"
                data-testid="push-already-granted"
              >
                Notificações já estão ativadas neste dispositivo.
              </p>
            ) : perm === "denied" ? (
              <p
                className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2"
                data-testid="push-denied"
              >
                Você bloqueou as notificações. Entre nas configurações do
                navegador para o site AMC Escolas e permita notificações.
              </p>
            ) : perm === "unsupported" ? (
              <p
                className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"
                data-testid="push-unsupported"
              >
                {ios && !standalone
                  ? "No iPhone, instale o app na tela inicial primeiro (passos acima). Depois abra-o pela tela inicial e volte aqui para ativar."
                  : "Este navegador não suporta notificações push. Use o Chrome, Edge, Firefox ou Safari (iOS 16.4+)."}
              </p>
            ) : (
              <Button
                onClick={enablePush}
                disabled={busy || (ios && !standalone)}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800 text-white font-bold shadow-lg shadow-purple-500/25"
                data-testid="install-dialog-enable-push"
              >
                {busy ? "Ativando..." : "Ativar notificações agora"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const BenefitCard = ({ icon: Icon, label, tint }) => (
  <div className="rounded-2xl bg-slate-50/80 border border-slate-100 px-2 py-3 flex flex-col items-center text-center gap-1.5">
    <div
      className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tint} text-white flex items-center justify-center shadow-md`}
    >
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </div>
    <p className="text-[10.5px] font-bold text-slate-700 leading-tight">
      {label}
    </p>
  </div>
);

const Step = ({ n, children, icon: Icon }) => (
  <li className="flex gap-3">
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#4338ca] text-white font-black text-xs flex items-center justify-center shrink-0 shadow-sm">
      {n}
    </div>
    <div className="flex-1 text-[13px] text-slate-700 flex items-center flex-wrap gap-1.5 pt-1">
      {children}
      {Icon && (
        <Icon className="w-4 h-4 text-[#7c3aed] inline-block" strokeWidth={2.4} />
      )}
    </div>
  </li>
);

const IOSSteps = () => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center">
        <Smartphone className="w-3.5 h-3.5" />
      </div>
      <p className="font-heading font-black text-sm text-[#1e3a8a]">
        Instalar no iPhone (Safari)
      </p>
    </div>
    <ol className="space-y-2.5">
      <Step n={1} icon={Share}>
        Toque no botão <span className="font-semibold">Compartilhar</span>
      </Step>
      <Step n={2} icon={Plus}>
        Role e toque em{" "}
        <span className="font-semibold">Adicionar à Tela de Início</span>
      </Step>
      <Step n={3}>
        Toque em <span className="font-semibold">Adicionar</span> no canto superior
        direito
      </Step>
      <Step n={4}>
        Abra o AMC Escolas pelo ícone criado na tela inicial e volte aqui para
        ativar as notificações
      </Step>
    </ol>
    <p className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
      Importante: no iPhone, as notificações só funcionam quando o app é aberto
      pelo ícone da tela inicial (iOS 16.4 ou superior).
    </p>
  </div>
);

const AndroidDesktopSteps = ({ onInstall }) => {
  const canInstall = supportsNativeInstall();
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
          <Smartphone className="w-3.5 h-3.5" />
        </div>
        <p className="font-heading font-black text-sm text-[#1e3a8a]">
          Instalar no Android / Computador
        </p>
      </div>
      {canInstall ? (
        <Button
          onClick={onInstall}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1e3a8a] to-[#4338ca] hover:from-[#1e40af] hover:to-[#4f46e5] text-white font-black text-[15px] shadow-lg shadow-blue-500/30"
          data-testid="install-native-btn"
        >
          Instalar aplicativo agora
        </Button>
      ) : (
        <ol className="space-y-2.5">
          <Step n={1}>
            Abra o menu do seu navegador (⋮ no Chrome / Edge)
          </Step>
          <Step n={2}>
            Toque em{" "}
            <span className="font-semibold">Instalar aplicativo</span> ou{" "}
            <span className="font-semibold">Adicionar à tela inicial</span>
          </Step>
          <Step n={3}>
            Confirme a instalação e abra o AMC Escolas pelo ícone criado
          </Step>
        </ol>
      )}
    </div>
  );
};
